"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Logo from "../Logo";

import {
  Menu,
  X,
  Search,
  ShoppingBag,
  User,
  Phone,
  Mail,
  Home,
  Package,
  ChevronRight,
  Info,
  Shield,
  Clipboard,
  NotebookPen,
} from "lucide-react";

import { useCart } from "@/app/context/web/CartContext";
import { apiurl } from "../apiurl";
import GoogleTranslate from "../GoogleTranslate";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [gettoken, setgettoken] = useState(null);

  const { cart } = useCart();

  let viewdata = () => {
    try {
      apiurl.get("/web/check-web-session").then((res) => {
        if (res.data.Status === 1) {
          return;
        } else {
          if (res.data.Redirect) {
            localStorage.removeItem("usertoken");

            sessionStorage.removeItem("csrftoken");

            setgettoken(null);
          }
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  /*
    |--------------------------------------------------------------------------
    | GET TOKEN
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    viewdata();

    const tokenvalue = JSON.parse(localStorage.getItem("usertoken"));

    setgettoken(tokenvalue ? tokenvalue : null);
  }, []);

  /*
    |--------------------------------------------------------------------------
    | BODY SCROLL LOCK
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";

      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";

      document.documentElement.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";

      document.documentElement.style.overflow = "";
    };
  }, [open]);

  /*
    |--------------------------------------------------------------------------
    | NAVIGATION LINKS
    |--------------------------------------------------------------------------
    */

  const navLinks = [
    {
      title: "Home",
      href: "/",
    },
    {
      title: "Products",
      href: "/products",
    },
    {
      title: "About",
      href: "/about",
    },
    {
      title: "Contact",
      href: "/contact",
    },
  ];

  /*
    |--------------------------------------------------------------------------
    | MOBILE MENU LINKS
    |--------------------------------------------------------------------------
    */

  const mobileLinks = [
    {
      title: "Home",
      href: "/",
      icon: Home,
    },
    {
      title: "Products",
      href: "/products",
      icon: Package,
    },
    {
      title: "About Us",
      href: "/about",
      icon: Info,
    },
    {
      title: "Contact",
      href: "/contact",
      icon: Phone,
    },
    {
      title: "Blogs",
      href: "/blogs",
      icon: NotebookPen,
    },
    {
      title: "Privacy",
      href: "/privacy",
      icon: Shield,
    },
    {
      title: "Terms & Conditions",
      href: "/terms",
      icon: Clipboard,
    },
  ];

  /*
    |--------------------------------------------------------------------------
    | CLOSE MOBILE MENU
    |--------------------------------------------------------------------------
    */

  const closeMenu = () => {
    setOpen(false);
  };

  return (
    <>
      {/* ==========================================================
                                TOP BAR
            ========================================================== */}

      <div className="bg-[#0A2342] text-white">
        <div className="mx-auto flex min-h-9.5 max-w-7xl items-center justify-center px-4 py-2 sm:px-6 lg:justify-between">
          {/* CONTACT DETAILS */}

          <div className="flex w-full items-center justify-center gap-4 sm:gap-6 lg:w-auto">
            {/* PHONE */}

            <a
              href="tel:+79165910278"
              className="flex items-center gap-1.5 whitespace-nowrap text-[11px] font-medium transition-colors hover:text-[#D4AF37] sm:gap-2 sm:text-xs md:text-sm"
            >
              <Phone size={13} className="shrink-0 sm:h-3.75 sm:w-3.75" />

              <span>+7 916-591-02-78</span>
            </a>

            {/* EMAIL */}

            <a
              href="mailto:vanshi-tex@mail.ru"
              className="flex items-center gap-1.5 whitespace-nowrap text-[11px] font-medium transition-colors hover:text-[#D4AF37] sm:gap-2 sm:text-xs md:text-sm"
            >
              <Mail size={13} className="shrink-0 sm:h-3.75 sm:w-3.75" />

              <span>vanshi-tex@mail.ru</span>
            </a>
          </div>

          {/* DESKTOP TAGLINE */}

          <div className="hidden text-xs uppercase tracking-[0.15em] lg:block">
            Premium Textile Wholesaler
          </div>
        </div>
      </div>

      {/* ==========================================================
                                MAIN HEADER
            ========================================================== */}

      <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 shadow-sm backdrop-blur-lg">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-6">
          <div className="flex h-18 items-center justify-between sm:h-20 lg:h-24">
            {/* ====================================================
                                            LOGO
                        ==================================================== */}

            <div className="w-28.75 shrink-0 sm:w-33.75 lg:w-35">
              <Link href="/" onClick={closeMenu} className="block">
                <Logo />
              </Link>
            </div>

            {/* ====================================================
                                        DESKTOP NAVIGATION
                        ==================================================== */}

            <nav className="hidden items-center gap-7 lg:flex xl:gap-10">
              {navLinks.map((item) => (
                <Link
                  key={item.title}
                  href={item.href}
                  className="group relative text-sm font-semibold uppercase tracking-wider text-[#0A2342]"
                >
                  {item.title}

                  <span className="absolute -bottom-2 left-0 h-0.5 w-0 bg-(--sand) transition-all duration-300 group-hover:w-full" />
                </Link>
              ))}
            </nav>

            {/* ====================================================
                                        DESKTOP RIGHT
                        ==================================================== */}

            <div className="hidden items-center gap-4 lg:flex xl:gap-5">
              {/* LANGUAGE */}

              <GoogleTranslate />

              {/* SEARCH */}

              <Link href="/products" aria-label="Search products">
                <Search
                  className="text-[#0A2342] transition hover:text-(--sand)"
                  size={22}
                />
              </Link>

              {/* CART */}

              <Link
                href="/pages/web/cart"
                className="group"
                aria-label="Shopping cart"
              >
                <div className="relative flex items-center justify-center">
                  <ShoppingBag
                    size={22}
                    strokeWidth={1.8}
                    className="text-[#0A2342] transition-all duration-300 group-hover:scale-110 group-hover:text-(--sand)"
                  />

                  {Array.isArray(cart) && cart.length > 0 && (
                    <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-[#0A2342] px-1 text-[10px] font-semibold leading-none text-white shadow-sm transition-all duration-300 group-hover:scale-110 group-hover:bg-(--sand) group-hover:text-[#0A2342]">
                      {cart.length > 99 ? "99+" : cart.length}
                    </span>
                  )}
                </div>
              </Link>

              {/* PROFILE / GET STARTED */}

              {gettoken === null ? (
                <Link
                  href={"/pages/web/auth/sign-up"}
                  type="button"
                  className="rounded-[10px] bg-[#0A2342] px-4 py-2.5 text-sm font-medium text-white transition-all duration-300 hover:bg-(--sand) hover:text-[#0A2342]"
                >
                  Get Started
                </Link>
              ) : (
                <Link href="/pages/web/profile" aria-label="Profile">
                  <User
                    className="text-[#0A2342] transition hover:text-(--sand)"
                    size={22}
                  />
                </Link>
              )}
            </div>

            {/* ====================================================
                                        MOBILE BUTTON
                        ==================================================== */}

            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              className="flex h-11 w-11 items-center justify-center rounded-full text-[#081C34] transition-all duration-300 hover:bg-gray-100 lg:hidden"
            >
              <Menu size={27} strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>

      {/* ==========================================================
                            MOBILE MENU OVERLAY
            ========================================================== */}

      <div
        className={`fixed inset-0 z-100 lg:hidden ${
          open ? "visible" : "pointer-events-none invisible"
        }`}
        onClick={closeMenu}
      >
        {/* ======================================================
                                    OVERLAY
                ====================================================== */}

        <div
          className={`absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* ======================================================
                                MOBILE SIDEBAR
                ====================================================== */}

        <aside
          onClick={(e) => e.stopPropagation()}
          className={`absolute right-0 top-0 flex h-100dvh w-full flex-col overflow-hidden bg-[#F7F7F7] shadow-2xl transition-transform duration-300 ease-out ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* ====================================================
                                    MOBILE HEADER
                    ==================================================== */}

          <div className="flex h-19 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-5 sm:h-20 sm:px-6">
            {/* LOGO */}

            <div className="w-31.25 sm:w-33.75">
              <Link href="/" onClick={closeMenu} className="block">
                <Logo />
              </Link>
            </div>

            {/* CLOSE */}

            <button
              type="button"
              onClick={closeMenu}
              aria-label="Close menu"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[#081C34] transition-all duration-300 hover:bg-gray-200"
            >
              <X size={20} />
            </button>
          </div>

          {/* ====================================================
                                MOBILE SCROLL AREA
                    ==================================================== */}

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 sm:py-6">
            {/* MENU TITLE */}

            <div className="mb-5 px-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">
                Navigation
              </p>

              <h2 className="mt-1 text-xl font-semibold text-[#0A2342]">
                Explore Vanshi Tex
              </h2>
            </div>

            {/* ==================================================
                                    MOBILE LANGUAGE
                        ================================================== */}

            <div className="mb-5">
              <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                Select Language
              </p>

              <GoogleTranslate mobile={true} />
            </div>

            {/* ==================================================
                                        LINKS
                        ================================================== */}

            <div className="space-y-2.5">
              {mobileLinks.map((item) => {
                const Icon = item.icon;

                return (
                  <Link
                    key={item.title}
                    href={item.href}
                    onClick={closeMenu}
                    className="group flex min-h-14 w-full items-center justify-between rounded-2xl border border-gray-100 bg-white px-4 shadow-sm transition-all duration-300 hover:border-[#D4AF37]/30 hover:shadow-md"
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F7F7F7] transition-all duration-300 group-hover:bg-[#0A2342]">
                        <Icon
                          size={20}
                          className="text-[#0A2342] transition-colors duration-300 group-hover:text-white"
                        />
                      </div>

                      <span className="truncate text-sm font-medium text-[#0A2342] sm:text-base">
                        {item.title}
                      </span>
                    </div>

                    <ChevronRight
                      size={18}
                      className="shrink-0 text-gray-400 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#D4AF37]"
                    />
                  </Link>
                );
              })}
            </div>

            {/* ==================================================
                                    MOBILE CONTACT BOX
                        ================================================== */}

            <div className="mb-10 mt-6 rounded-2xl bg-[#0A2342] p-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                Contact Us
              </p>

              <div className="mt-4 space-y-3">
                {/* PHONE */}

                <a
                  href="tel:+79165910278"
                  className="flex items-center gap-3 text-sm text-white"
                >
                  <Phone size={17} className="shrink-0 text-[#D4AF37]" />

                  <span>+7 916-591-02-78</span>
                </a>

                {/* EMAIL */}

                <a
                  href="mailto:vanshi-tex@mail.ru"
                  className="flex items-center gap-3 text-sm text-white"
                >
                  <Mail size={17} className="shrink-0 text-[#D4AF37]" />

                  <span className="break-all">vanshi-tex@mail.ru</span>
                </a>
              </div>
            </div>

            {/* EXTRA BOTTOM SPACE */}

            <div className="h-6" />
          </div>
        </aside>
      </div>
    </>
  );
}
